/**
 * Published Reports Dashboard Plugin — entry point and registration
 * (publish-report spec, Req 5.1; FINN 3 / F1 "Publish answer as dashboard").
 *
 * Lists the Ask AI answers published to the reader (global, team public, or
 * their own) and renders a selected one with the chat's markdown renderer.
 * Visible to every user: the resolver applies the visibility scope server-side,
 * so an empty team simply sees an empty list.
 */

import React from "react";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import type {
  DashboardPlugin,
  PluginDataResult,
  PluginFilterPanelProps,
} from "../../types";
import PublishedReportsContent from "./PublishedReportsContent";
import { PUBLISHED_REPORTS_DASHBOARD_ID } from "../../../publishedReports/publishedReportModel";

/** The content owns its own filter chips; the shell-level panel renders nothing. */
const PublishedReportsFilterPanel: React.FC<PluginFilterPanelProps> = () =>
  null;

/**
 * The content fetches its own data (getPublishedReports), so the shell-level
 * hook is inert: never loading, no error, nothing to refetch. `data` is a
 * non-null constant so DashboardShell's first-load banner never shows (same
 * shape as the Audit Console plugin).
 */
const PUBLISHED_REPORTS_DATA: PluginDataResult = {
  data: {},
  isLoading: false,
  error: null,
  refetch: () => {},
};
const usePublishedReportsData = (): PluginDataResult => PUBLISHED_REPORTS_DATA;

export const publishedReportsDashboard: DashboardPlugin = {
  id: PUBLISHED_REPORTS_DASHBOARD_ID,
  displayName: "Published Reports",
  icon: <ArticleOutlinedIcon />,
  description: "Ask AI answers published as reports for your team",

  FilterPanel: PublishedReportsFilterPanel,
  ReportContent: PublishedReportsContent,
  useData: usePublishedReportsData,

  // No AI summary, no second-table comparison, and no CSV export — the shell
  // hides the CSV button when a plugin declares no `exportCsv`; answer tables
  // carry their own Download CSV control.
  supportsComparison: false,
};
